import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AdminGate from "../components/admin/AdminGate";
import { adminFetch, forceReauth } from "../lib/admin";

/**
 * AdminDashboardPage — the unified editorial system at /admin.
 *
 * One table for every Idea, Research entry and Project, with overview
 * totals, recently-updated list, type/status filters, search and date
 * sorting. "+ New Entry" asks for the content type first; the editor
 * form then adapts to the chosen type.
 */

const TYPES = [
    { key: "ideas", label: "Ideas", singular: "Idea", tag: "IDEA" },
    { key: "research", label: "Research", singular: "Research", tag: "RESEARCH" },
    { key: "projects", label: "Projects", singular: "Project", tag: "PROJECT" },
    { key: "memoire", label: "Memoire", singular: "Memoire", tag: "MEMOIRE" },
];
const TYPE_META = Object.fromEntries(TYPES.map((t) => [t.key, t]));

const FILTERS = [
    { key: "all", label: "All" },
    { key: "ideas", label: "Ideas" },
    { key: "research", label: "Research" },
    { key: "projects", label: "Projects" },
    { key: "memoire", label: "Memoire" },
    { key: "drafts", label: "Drafts" },
    { key: "published", label: "Published" },
];

const SORTS = [
    { key: "updated-desc", label: "Recently updated" },
    { key: "updated-asc", label: "Least recently updated" },
    { key: "published-desc", label: "Newest published" },
    { key: "published-asc", label: "Oldest published" },
];

function fmtDate(v) {
    if (!v) return "—";
    const d = new Date(v);
    return Number.isNaN(d.getTime())
        ? String(v)
        : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

const ts = (v) => {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

function Stat({ label, value, testId }) {
    return (
        <div data-testid={testId} className="border-t border-[color:var(--line-strong)] pt-4">
            <div className="font-display font-light text-[34px] md:text-[40px] leading-none text-[color:var(--navy)]">{value}</div>
            <div className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">{label}</div>
        </div>
    );
}

function Dashboard() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const filter = FILTERS.some((f) => f.key === params.get("filter")) ? params.get("filter") : "all";
    const sort = SORTS.some((s) => s.key === params.get("sort")) ? params.get("sort") : "updated-desc";
    const query = params.get("q") || "";

    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState(null);
    const [confirmId, setConfirmId] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);

    const load = useCallback(async () => {
        setBusy(true);
        setError(null);
        try {
            const resps = await Promise.all(TYPES.map((t) => adminFetch(`/admin/content/${t.key}`)));
            if (resps.some((r) => r.status === 401)) { forceReauth(); return; }
            const failed = resps.find((r) => !r.ok);
            if (failed) { setError(`Could not load content (HTTP ${failed.status}).`); return; }
            const lists = await Promise.all(resps.map((r) => r.json()));
            setItems(lists.flatMap((list, i) => list.map((x) => ({ ...x, _type: TYPES[i].key }))));
        } catch (_e) {
            setError("Network error — content could not be loaded.");
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const overview = useMemo(() => ({
        ideas: items.filter((x) => x._type === "ideas").length,
        research: items.filter((x) => x._type === "research").length,
        projects: items.filter((x) => x._type === "projects").length,
        memoire: items.filter((x) => x._type === "memoire").length,
        drafts: items.filter((x) => x.status !== "published").length,
        published: items.filter((x) => x.status === "published").length,
    }), [items]);

    const recent = useMemo(
        () => [...items].sort((a, b) => ts(b.updated_at) - ts(a.updated_at)).slice(0, 5),
        [items]
    );

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        let out = items.filter((x) => {
            if (filter === "drafts" && x.status === "published") return false;
            if (filter === "published" && x.status !== "published") return false;
            if (["ideas", "research", "projects", "memoire"].includes(filter) && x._type !== filter) return false;
            if (q && !(`${x.title} ${x.slug}`.toLowerCase().includes(q))) return false;
            return true;
        });
        const key = sort.startsWith("published") ? "published_at" : "updated_at";
        const dir = sort.endsWith("asc") ? 1 : -1;
        out.sort((a, b) => dir * (ts(a[key]) - ts(b[key])));
        return out;
    }, [items, filter, query, sort]);

    const setParam = (k, v) => {
        const next = Object.fromEntries(params.entries());
        if (v) next[k] = v; else delete next[k];
        setParams(next, { replace: true });
        setConfirmId(null);
    };

    const toggleStatus = async (item) => {
        const next = item.status === "published" ? "draft" : "published";
        try {
            const resp = await adminFetch(`/admin/content/${item._type}/${item.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...item, status: next }),
            });
            if (resp.status === 401) { forceReauth(); return; }
            if (resp.ok) load();
        } catch (_e) { /* noop */ }
    };

    const remove = async (item) => {
        try {
            const resp = await adminFetch(`/admin/content/${item._type}/${item.id}`, { method: "DELETE" });
            if (resp.status === 401) { forceReauth(); return; }
            if (resp.ok) setItems((xs) => xs.filter((x) => x.id !== item.id));
        } catch (_e) { /* noop */ }
        setConfirmId(null);
    };

    return (
        <div className="mt-10" data-testid="admin-dashboard">
            {/* ---------- Overview ---------- */}
            <section aria-label="Overview" data-testid="admin-overview">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                    <Stat testId="stat-ideas" label="Ideas" value={overview.ideas} />
                    <Stat testId="stat-research" label="Research" value={overview.research} />
                    <Stat testId="stat-projects" label="Projects" value={overview.projects} />
                    <Stat testId="stat-memoire" label="Memoire" value={overview.memoire} />
                    <Stat testId="stat-drafts" label="Drafts" value={overview.drafts} />
                    <Stat testId="stat-published" label="Published" value={overview.published} />
                </div>
                <div className="mt-10" data-testid="admin-recent">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-3">Recently updated</div>
                    {recent.length === 0 && !busy ? (
                        <p className="text-[14px] text-[color:var(--warm-gray-2)]">Nothing yet.</p>
                    ) : (
                        <ul className="divide-y divide-[color:var(--line)] border-y border-[color:var(--line)]">
                            {recent.map((x) => (
                                <li key={x.id} data-testid="admin-recent-row">
                                    <Link
                                        to={`/admin/content/${x._type}/${x.id}/edit`}
                                        className="py-3 flex items-baseline justify-between gap-4 group"
                                    >
                                        <span className="font-display text-[16px] text-[color:var(--navy)] group-hover:italic transition-all truncate">{x.title}</span>
                                        <span className="shrink-0 font-mono text-[10px] tracking-[0.18em] uppercase text-[color:var(--warm-gray-2)]">
                                            {TYPE_META[x._type].tag} · {x.status === "published" ? "Published" : "Draft"} · {fmtDate(x.updated_at)}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            {/* ---------- Content ---------- */}
            <section aria-label="Content" className="mt-14" data-testid="admin-content">
                <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[color:var(--line-strong)] pt-6">
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2" role="tablist" aria-label="Filter">
                        {FILTERS.map((f) => (
                            <button
                                key={f.key}
                                type="button"
                                role="tab"
                                aria-selected={filter === f.key}
                                data-testid={`admin-filter-${f.key}`}
                                onClick={() => setParam("filter", f.key === "all" ? "" : f.key)}
                                className={`font-mono text-[10px] tracking-[0.22em] uppercase transition-colors duration-300 ${
                                    filter === f.key ? "text-[color:var(--navy)] border-b border-[color:var(--gold)] pb-1" : "text-[color:var(--warm-gray-2)] hover:text-[color:var(--navy)]"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        data-testid="admin-new-entry-button"
                        onClick={() => setPickerOpen(true)}
                        className="btn-editorial"
                    >
                        + New Entry
                        <span aria-hidden className="w-4 h-px bg-current opacity-70" />
                    </button>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-4">
                    <input
                        type="search"
                        data-testid="admin-search-input"
                        value={query}
                        onChange={(e) => setParam("q", e.target.value)}
                        placeholder="Search title or slug…"
                        aria-label="Search entries"
                        className="w-full md:w-[300px] bg-transparent border border-[color:var(--line-strong)] px-4 py-2.5 text-[14px] text-[color:var(--navy)] placeholder:text-[color:var(--warm-gray-2)]/70 focus:outline-none focus:border-[color:var(--gold)] transition-colors"
                    />
                    <select
                        data-testid="admin-sort-select"
                        value={sort}
                        onChange={(e) => setParam("sort", e.target.value === "updated-desc" ? "" : e.target.value)}
                        aria-label="Sort by date"
                        className="bg-transparent border border-[color:var(--line-strong)] px-3 py-2.5 text-[13px] text-[color:var(--navy)] focus:outline-none focus:border-[color:var(--gold)] transition-colors"
                    >
                        {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                </div>

                {error && (
                    <p data-testid="admin-content-error" role="alert" className="mt-6 text-[13px] text-[#9A3B26]">{error}</p>
                )}
                {!error && !busy && visible.length === 0 && (
                    <p data-testid="admin-content-empty" className="font-display italic text-[22px] leading-[1.35] text-[color:var(--navy)]/85 py-16 border-b border-[color:var(--line-strong)]">
                        Nothing matches — start with “+ New Entry”.
                    </p>
                )}

                {/* header row (desktop) */}
                <div className="hidden md:grid grid-cols-12 gap-4 mt-8 pb-3 border-b border-[color:var(--line-strong)] font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--warm-gray-2)]" aria-hidden>
                    <div className="col-span-4">Title</div>
                    <div className="col-span-2">Type</div>
                    <div className="col-span-1">Status</div>
                    <div className="col-span-1">Published</div>
                    <div className="col-span-1">Updated</div>
                    <div className="col-span-3 text-right">Actions</div>
                </div>

                <ul>
                    {visible.map((item) => (
                        <li
                            key={item.id}
                            data-testid="admin-content-row"
                            className="border-b border-[color:var(--line)] py-5 grid grid-cols-12 gap-x-4 gap-y-3 items-baseline"
                        >
                            <div className="col-span-12 md:col-span-4 min-w-0">
                                <span className="font-display text-[18px] leading-[1.25] text-[color:var(--navy)] block truncate">{item.title}</span>
                                <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-[color:var(--warm-gray-2)] block truncate">
                                    /{item._type}/{item.slug}
                                </span>
                            </div>
                            <div className="col-span-4 md:col-span-2 font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--navy)]" data-testid="admin-row-type">
                                {TYPE_META[item._type].tag}
                            </div>
                            <div className="col-span-4 md:col-span-1" data-testid="admin-row-status">
                                <span className={`inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase ${item.status === "published" ? "text-[color:var(--navy)]" : "text-[color:var(--warm-gray-2)]"}`}>
                                    <span aria-hidden className={`w-[7px] h-[7px] rounded-full ${item.status === "published" ? "bg-[color:var(--gold)]" : "bg-[color:var(--line-strong)]"}`} />
                                    {item.status === "published" ? "Published" : "Draft"}
                                </span>
                            </div>
                            <div className="col-span-2 md:col-span-1 font-mono text-[10px] tracking-[0.14em] uppercase text-[color:var(--warm-gray-2)]" data-testid="admin-row-published">
                                {item.publication_date || fmtDate(item.published_at)}
                            </div>
                            <div className="col-span-2 md:col-span-1 font-mono text-[10px] tracking-[0.14em] uppercase text-[color:var(--warm-gray-2)]" data-testid="admin-row-updated">
                                {fmtDate(item.updated_at)}
                            </div>
                            <div className="col-span-12 md:col-span-3 flex flex-wrap items-center md:justify-end gap-x-5 gap-y-2">
                                <Link
                                    to={`/admin/content/${item._type}/${item.id}/edit`}
                                    data-testid="admin-edit-button"
                                    className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase"
                                >
                                    Edit
                                </Link>
                                {item.preview_token && (
                                    <a
                                        href={`/preview/${item.preview_token}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="admin-preview-button"
                                        className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase"
                                    >
                                        Preview
                                    </a>
                                )}
                                <button
                                    type="button"
                                    data-testid={item.status === "published" ? "admin-unpublish-button" : "admin-publish-button"}
                                    onClick={() => toggleStatus(item)}
                                    className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase"
                                >
                                    {item.status === "published" ? "Unpublish" : "Publish"}
                                </button>
                                {confirmId === item.id ? (
                                    <>
                                        <button
                                            type="button"
                                            data-testid="admin-confirm-delete-button"
                                            onClick={() => remove(item)}
                                            className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[#9A3B26]"
                                        >
                                            Confirm delete
                                        </button>
                                        <button
                                            type="button"
                                            data-testid="admin-cancel-delete-button"
                                            onClick={() => setConfirmId(null)}
                                            className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase"
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        type="button"
                                        data-testid="admin-delete-button"
                                        onClick={() => setConfirmId(item.id)}
                                        className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]"
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </section>

            {/* ---------- New Entry type picker ---------- */}
            {pickerOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--navy)]/30 px-6"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Choose content type"
                    data-testid="admin-type-picker"
                    onClick={() => setPickerOpen(false)}
                >
                    <div
                        className="bg-[color:var(--ivory)] border border-[color:var(--line-strong)] max-w-[560px] w-full p-8 md:p-10"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-2">New entry</div>
                        <h2 className="font-display font-light text-[28px] leading-[1.1] text-[color:var(--navy)] mb-8">
                            What are you creating?
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {TYPES.map((t) => (
                                <button
                                    key={t.key}
                                    type="button"
                                    data-testid={`admin-pick-${t.key}`}
                                    onClick={() => navigate(`/admin/content/${t.key}/new`)}
                                    className="border border-[color:var(--line-strong)] px-4 py-6 text-center group hover:border-[color:var(--gold)] transition-colors duration-300"
                                >
                                    <span className="font-display text-[20px] text-[color:var(--navy)] group-hover:italic transition-all">{t.singular}</span>
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            data-testid="admin-picker-cancel"
                            onClick={() => setPickerOpen(false)}
                            className="mt-8 link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AdminDashboardPage() {
    return (
        <AdminGate title="Editorial.">
            <Dashboard />
        </AdminGate>
    );
}
